import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminGetToken } from "@/lib/admin";

export default function AdminLogin() {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const nav = useNavigate();

    if (adminGetToken()) {
        return <Navigate to="/admin" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            await adminLogin(password);
            toast.success("Welcome back.");
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error("Invalid password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] grid place-items-center px-5">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="flex items-center justify-center gap-3 mb-8">
                    <div className="h-12 w-12 grid place-items-center border border-[#D4AF37]">
                        <span className="font-display text-2xl text-[#D4AF37]">
                            YM
                        </span>
                    </div>
                    <div>
                        <div className="font-display text-base uppercase tracking-wider text-white leading-none">
                            EDUtainment Pros
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">
                            Admin Console
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="glass-gold p-8 md:p-10 space-y-5"
                    data-testid="admin-login-form"
                >
                    <div className="flex items-center gap-3 mb-2">
                        <Lock className="text-[#D4AF37]" size={18} />
                        <h1 className="font-display text-2xl uppercase tracking-tight text-white">
                            Restricted Access
                        </h1>
                    </div>
                    <p className="text-sm text-white/60">
                        Enter the admin password to view submitted applications,
                        partnerships, and contacts.
                    </p>

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                            Password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoFocus
                            data-testid="admin-password-input"
                            placeholder="••••••••"
                            className="mt-2 w-full bg-black/40 border border-white/10 px-4 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || !password}
                        data-testid="admin-login-submit"
                        className="w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black font-display uppercase tracking-widest py-4 hover:bg-[#FFF3A1] transition disabled:opacity-50"
                    >
                        {loading ? "Verifying..." : "Sign In"}
                        <ArrowRight size={16} />
                    </button>
                </form>
                <div className="mt-6 text-center">
                    <a
                        href="/"
                        className="text-xs uppercase tracking-[0.3em] text-white/40 hover:text-[#D4AF37]"
                    >
                        ← Back to site
                    </a>
                </div>
            </motion.div>
        </div>
    );
}
